/**
 * LeadToolCards — Financing quote + trade-in estimate context cards for
 * BDC, dealer, and admin lead views.
 *
 * The parent lead payload already embeds `financing_quote` and
 * `trade_in_estimate` when the lead was created from those tools. These
 * components render nothing if the field is missing, so it's safe to drop
 * them into any lead detail template.
 */
import React from "react";
import { Banknote, Car, Info } from "lucide-react";

const fmtUSD = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format((cents || 0) / 100);
const fmtUSDDec = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((cents || 0) / 100);

export const FinancingQuoteCard = ({ quote, className = "" }) => {
  if (!quote) return null;
  const apr = (quote.apr_bps / 100).toFixed(2);
  return (
    <div
      className={`rounded-2xl border-2 border-emerald-500/60 bg-white p-4 ${className}`}
      data-testid="lead-financing-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center">
          <Banknote size={13} />
        </span>
        <p className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Financing quote</p>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Monthly</p>
          <p className="font-display font-bold text-slate-900 text-lg" data-testid="lead-financing-monthly">
            {fmtUSD(quote.monthly_payment_cents)}<span className="text-xs text-slate-400 font-normal">/mo</span>
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Term</p>
          <p className="font-semibold text-slate-900">{quote.loan_term_months} months</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">APR</p>
          <p className="font-semibold text-slate-900">{apr}% <span className="text-xs text-slate-400 font-normal capitalize">({quote.credit_band})</span></p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Down payment</p>
          <p className="font-semibold text-slate-900">{fmtUSD(quote.down_payment_cents)}</p>
        </div>
        {quote.trade_in_credit_cents ? (
          <div className="col-span-2">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Trade-in credit applied</p>
            <p className="font-semibold text-slate-900">{fmtUSD(quote.trade_in_credit_cents)}</p>
          </div>
        ) : null}
        <div className="col-span-2 border-t border-slate-100 pt-2 mt-1">
          <p className="text-[11px] text-slate-500">
            Loan: <strong className="text-slate-800">{fmtUSDDec(quote.principal_cents)}</strong>
            {" · "}
            Total interest: <strong className="text-slate-800">{fmtUSDDec(quote.total_interest_cents)}</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export const TradeInEstimateCard = ({ estimate, className = "" }) => {
  if (!estimate) return null;
  return (
    <div
      className={`rounded-2xl border-2 border-gold/70 bg-white p-4 ${className}`}
      data-testid="lead-tradein-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="w-7 h-7 rounded-full bg-gold text-slate-900 flex items-center justify-center">
          <Car size={13} />
        </span>
        <p className="text-xs uppercase tracking-widest font-semibold text-[#7A5C0E]">Trade-in estimate</p>
      </div>
      <p className="font-display text-lg font-semibold text-slate-900">
        {estimate.year} {estimate.make} {estimate.model}
        {estimate.trim && <span className="text-sm text-slate-500 font-normal"> ({estimate.trim})</span>}
      </p>
      <p className="text-xs text-slate-500 mt-0.5">
        {estimate.mileage?.toLocaleString()} mi · {estimate.condition} condition
        {estimate.zip_code ? ` · ZIP ${estimate.zip_code}` : ""}
      </p>
      <div className="mt-2 rounded-lg bg-gold/10 border border-gold/30 px-3 py-2">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Estimated range</p>
        <p className="font-display text-xl font-bold text-slate-900" data-testid="lead-tradein-range">
          {fmtUSD(estimate.estimate_low_cents)} – {fmtUSD(estimate.estimate_high_cents)}
        </p>
        <p className="text-[11px] text-slate-600 mt-0.5">Mid: <strong>{fmtUSD(estimate.estimate_mid_cents)}</strong></p>
      </div>
      {estimate.notes && (
        <p className="mt-2 text-xs text-slate-600 italic border-l-2 border-slate-200 pl-2">
          "{estimate.notes}"
        </p>
      )}
      <p className="mt-2 text-[10px] text-slate-400 flex items-start gap-1">
        <Info size={9} className="shrink-0 mt-0.5" />
        Estimate is a starting point — final value requires an in-person appraisal.
      </p>
    </div>
  );
};

/**
 * Convenience combo — renders both cards in sequence (if present).
 * Returns null if neither is present, so it's safe to always mount.
 */
export const LeadToolCards = ({ lead, className = "" }) => {
  if (!lead) return null;
  const fq = lead.financing_quote;
  const ti = lead.trade_in_estimate;
  if (!fq && !ti) return null;
  return (
    <div className={`space-y-3 ${className}`}>
      <FinancingQuoteCard quote={fq} />
      <TradeInEstimateCard estimate={ti} />
    </div>
  );
};

export default LeadToolCards;
